import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import SEO, { ORG_LD, SITE_LD } from "@/components/SEO";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Signup from "@/pages/Signup";
import PublicScreen from "@/pages/PublicScreen";
import FranchiseApply from "@/pages/FranchiseApply";
import AppShell from "@/layouts/AppShell";
import CustomerDashboard from "@/pages/customer/Dashboard";
import Hubs from "@/pages/customer/Hubs";
import HubDetail from "@/pages/customer/HubDetail";
import Memberships from "@/pages/customer/Memberships";
import Wallet from "@/pages/customer/Wallet";
import MyBookings from "@/pages/customer/MyBookings";
import AdminConsole from "@/pages/admin/AdminConsole";
import LeadsInbox from "@/pages/admin/LeadsInbox";
import AdminPartners from "@/pages/admin/AdminPartners";
import { AdminCommissions, AdminMarketplace } from "@/pages/admin/AdminPartnerOps";
import AdminGovernance from "@/pages/admin/AdminGovernance";
import Partners from "@/pages/partners/Partners";
import {
  PartnersApply,
  PartnersThanks,
  PartnersDirectory,
} from "@/pages/partners/PartnersApplyAndDirectory";
import PartnerCommandCenter from "@/pages/partners/PartnerCommandCenter";
import HQDashboard from "@/pages/hq/HQDashboard";
import Institutions from "@/pages/hq/Institutions";
import HQFranchises from "@/pages/hq/HQFranchises";
import FranchiseDashboard from "@/pages/franchise/FranchiseDashboard";
import AdsDashboard from "@/pages/ads/AdsDashboard";
import AdReviewHQ from "@/pages/ads/AdReviewHQ";
import SignageDashboard from "@/pages/signage/SignageDashboard";
import PlayScreen from "@/pages/signage/PlayScreen";
import RemoteControl from "@/pages/signage/RemoteControl";
import DemoIndex from "@/pages/demo/DemoIndex";
import DemoAdvertise from "@/pages/demo/DemoAdvertise";
import DemoSignage from "@/pages/demo/DemoSignage";
import NetworkExplorer from "@/pages/network/NetworkExplorer";
import Programmable from "@/pages/programmable/Programmable";
import Venues from "@/pages/venues/Venues";
import MediaOwners from "@/pages/mediaowners/MediaOwners";
import Calculator from "@/pages/mediaowners/Calculator";
import { NodePage, OSPage, MPMPPage } from "@/pages/labs/ConceptPages";
import Explore from "@/pages/Explore";
import Verify from "@/pages/Verify";
import PreviewCampaign from "@/pages/PreviewCampaign";
import AuditArchive from "@/pages/AuditArchive";
import ProofOfPlay from "@/pages/signage/ProofOfPlay";
import CampaignBuilder from "@/pages/ads/CampaignBuilder";
import RuleMarketplace from "@/pages/ads/RuleMarketplace";
import Concierge from "@/pages/Concierge";
import Downloads from "@/pages/Downloads";
import KioskLock from "@/pages/KioskLock";
import { StoreHome, StoreSearch, StoreProduct } from "@/pages/store/Store";
import BOMBuilder from "@/pages/store/BOMBuilder";
import { StoreBundles, StoreBundleDetail } from "@/pages/store/Bundles";
import { StoreCompare, StoreProjects, StoreProjectDetail } from "@/pages/store/CompareAndProjects";
import CompareSnapshot from "@/pages/store/CompareSnapshot";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0908] text-zinc-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SEO
          jsonLd={{
            "@context": "https://schema.org",
            "@graph": [ORG_LD, SITE_LD],
          }}
        />
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/s/*" element={<PublicScreen />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/partners/apply" element={<PartnersApply />} />
          <Route path="/partners/thanks/:appId" element={<PartnersThanks />} />
          <Route path="/partners/directory" element={<PartnersDirectory />} />
          <Route path="/franchise/apply" element={<FranchiseApply />} />
          <Route path="/play/:token" element={<PlayScreen />} />
          <Route path="/remote/:code" element={<RemoteControl />} />
          <Route path="/demo" element={<DemoIndex />} />
          <Route path="/demo/advertise" element={<DemoAdvertise />} />
          <Route path="/demo/signage" element={<DemoSignage />} />
          <Route path="/demo/player" element={<Navigate to="/play/demo" replace />} />
          <Route path="/network" element={<NetworkExplorer />} />
          <Route path="/programmable" element={<Programmable />} />
          <Route path="/venues" element={<Venues />} />
          <Route path="/media-owners" element={<MediaOwners />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/node" element={<NodePage />} />
          <Route path="/os" element={<OSPage />} />
          <Route path="/labs/mpmp" element={<MPMPPage />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/preview/campaign/:token" element={<PreviewCampaign />} />
          <Route path="/audit/:handle" element={<AuditArchive />} />
          <Route path="/concierge" element={<Concierge />} />
          <Route path="/store" element={<StoreHome />} />
          <Route path="/store/search" element={<StoreSearch />} />
          <Route path="/store/build" element={<BOMBuilder />} />
          <Route path="/store/bundles" element={<StoreBundles />} />
          <Route path="/store/bundles/:slug" element={<StoreBundleDetail />} />
          <Route path="/store/compare" element={<StoreCompare />} />
          <Route path="/store/compare/s/:sid" element={<CompareSnapshot />} />
          <Route path="/store/projects" element={<StoreProjects />} />
          <Route path="/store/projects/:pid" element={<StoreProjectDetail />} />
          <Route path="/store/product/:pid" element={<StoreProduct />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/kiosk-lock" element={<KioskLock />} />

          {/* App routes */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<RoleHome />} />
            <Route path="dashboard" element={<CustomerDashboard />} />
            <Route path="hubs" element={<Hubs />} />
            <Route path="hubs/:hubId" element={<HubDetail />} />
            <Route path="memberships" element={<Memberships />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="bookings" element={<MyBookings />} />
            <Route
              path="admin"
              element={
                <ProtectedRoute roles={["staff", "manager", "hq_admin"]}>
                  <AdminConsole />
                </ProtectedRoute>
              }
            />
            <Route
              path="hq"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <HQDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="institutions"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <Institutions />
                </ProtectedRoute>
              }
            />
            <Route
              path="franchise"
              element={
                <ProtectedRoute roles={["franchise_owner", "hq_admin", "manager"]}>
                  <FranchiseDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="franchises"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <HQFranchises />
                </ProtectedRoute>
              }
            />
            <Route path="ads" element={<AdsDashboard />} />
            <Route path="campaigns/new" element={<CampaignBuilder />} />
            <Route path="rules/marketplace" element={<RuleMarketplace />} />
            <Route path="proof-of-play" element={<ProofOfPlay />} />
            <Route
              path="ads/review"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <AdReviewHQ />
                </ProtectedRoute>
              }
            />
            <Route path="signage" element={<SignageDashboard />} />
            <Route
              path="partner"
              element={
                <ProtectedRoute>
                  <PartnerCommandCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/partners"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <AdminPartners />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/commissions"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <AdminCommissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/marketplace"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <AdminMarketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/governance"
              element={
                <ProtectedRoute roles={["hq_admin", "manager"]}>
                  <AdminGovernance />
                </ProtectedRoute>
              }
            />
            <Route
              path="leads"
              element={
                <ProtectedRoute roles={["hq_admin"]}>
                  <LeadsInbox />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" />
      </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "hq_admin") return <Navigate to="/app/hq" replace />;
  if (user?.role === "franchise_owner") return <Navigate to="/app/franchise" replace />;
  if (user?.role === "manager" || user?.role === "staff")
    return <Navigate to="/app/admin" replace />;
  return <Navigate to="/app/dashboard" replace />;
}

export default App;
